import { useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Tab, Listbox } from '@headlessui/react';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { examApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import clsx from 'clsx';

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  PLANNING: { label: 'Planning', className: 'bg-gray-100 text-gray-700' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
  FIELDWORK_COMPLETE: { label: 'Fieldwork Complete', className: 'bg-yellow-100 text-yellow-700' },
  REPORT_PENDING: { label: 'Report Pending', className: 'bg-orange-100 text-orange-700' },
  CLOSED: { label: 'Closed', className: 'bg-green-100 text-green-700' },
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  PLANNING: ['IN_PROGRESS'],
  IN_PROGRESS: ['FIELDWORK_COMPLETE'],
  FIELDWORK_COMPLETE: ['REPORT_PENDING'],
  REPORT_PENDING: ['CLOSED'],
  CLOSED: [],
};

const EXAM_TYPES: Record<string, string> = {
  FDIC_SAFETY_SOUNDNESS: 'FDIC Safety & Soundness',
  FDIC_COMPLIANCE: 'FDIC Compliance',
  OCC: 'OCC',
  STATE: 'State',
  CFPB: 'CFPB',
  CRA: 'CRA',
  BSA_AML: 'BSA/AML',
  NCUA: 'NCUA',
  JOINT: 'Joint',
  OTHER: 'Other',
};

const DOC_CATEGORIES = [
  { value: 'MARKETING_COMPLIANCE', label: 'Marketing Compliance' },
  { value: 'HMDA', label: 'HMDA' },
  { value: 'FAIR_LENDING', label: 'Fair Lending' },
  { value: 'POLICIES', label: 'Policies' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'BOARD_MINUTES', label: 'Board Minutes' },
  { value: 'AUDIT_REPORTS', label: 'Audit Reports' },
  { value: 'COMPLAINT_LOG', label: 'Complaint Log' },
  { value: 'BSA', label: 'BSA' },
  { value: 'CRA', label: 'CRA' },
  { value: 'OTHER', label: 'Other' },
];

const DOC_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  PENDING_PREPARATION: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  READY: { label: 'Ready', className: 'bg-green-100 text-green-700' },
  SHARED_WITH_EXAMINER: { label: 'Shared', className: 'bg-blue-100 text-blue-700' },
  ACKNOWLEDGED: { label: 'Acknowledged', className: 'bg-purple-100 text-purple-700' },
};

const SEVERITY_CONFIG: Record<string, { label: string; className: string }> = {
  CRITICAL: { label: 'Critical', className: 'bg-red-100 text-red-700' },
  HIGH: { label: 'High', className: 'bg-orange-100 text-orange-700' },
  MEDIUM: { label: 'Medium', className: 'bg-yellow-100 text-yellow-700' },
  LOW: { label: 'Low', className: 'bg-gray-100 text-gray-600' },
};

const REMEDIATION_CONFIG: Record<string, { label: string; className: string }> = {
  OPEN: { label: 'Open', className: 'bg-red-100 text-red-700' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
  REMEDIATED: { label: 'Remediated', className: 'bg-yellow-100 text-yellow-700' },
  VERIFIED: { label: 'Verified', className: 'bg-green-100 text-green-700' },
  CLOSED: { label: 'Closed', className: 'bg-gray-100 text-gray-600' },
};

const REQUEST_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  RECEIVED: { label: 'Received', className: 'bg-gray-100 text-gray-700' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
  FULFILLED: { label: 'Fulfilled', className: 'bg-green-100 text-green-700' },
  UNABLE_TO_FULFILL: { label: 'Unable', className: 'bg-red-100 text-red-700' },
};

const PRIORITY_CONFIG: Record<string, { label: string; className: string }> = {
  URGENT: { label: 'Urgent', className: 'text-red-600 font-semibold' },
  NORMAL: { label: 'Normal', className: 'text-gray-600' },
  LOW: { label: 'Low', className: 'text-gray-400' },
};

export default function ExamDetailPage() {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showAddFinding, setShowAddFinding] = useState(false);
  const [showAddRequest, setShowAddRequest] = useState(false);

  // Team
  const { teamId } = useTeamId();

  // Exam detail
  const { data: examData, isLoading } = useQuery({
    queryKey: ['exam', teamId, examId],
    queryFn: () => examApi.get(teamId!, examId!),
    enabled: !!teamId && !!examId,
  });
  const exam = examData?.data;

  // Readiness
  const { data: readinessData } = useQuery({
    queryKey: ['examReadiness', teamId, examId],
    queryFn: () => examApi.getReadiness(teamId!, examId!),
    enabled: !!teamId && !!examId,
  });
  const readiness = readinessData?.data;

  // Status change
  const statusMutation = useMutation({
    mutationFn: (status: string) => examApi.updateStatus(teamId!, examId!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam'] });
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      toast.success('Status updated');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to update status'),
  });

  // ─── Documents ──────────────────────────────────────────────────────────────
  const [docForm, setDocForm] = useState({ documentName: '', documentCategory: 'OTHER', notes: '' });

  const addDocMutation = useMutation({
    mutationFn: (data: any) => examApi.addDocument(teamId!, examId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam'] });
      queryClient.invalidateQueries({ queryKey: ['examReadiness'] });
      toast.success('Document added');
      setShowAddDoc(false);
      setDocForm({ documentName: '', documentCategory: 'OTHER', notes: '' });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to add document'),
  });

  const docStatusMutation = useMutation({
    mutationFn: ({ docId, status }: { docId: string; status: string }) =>
      examApi.updateDocumentStatus(teamId!, examId!, docId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam'] });
      queryClient.invalidateQueries({ queryKey: ['examReadiness'] });
      toast.success('Document status updated');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed'),
  });

  const deleteDocMutation = useMutation({
    mutationFn: (docId: string) => examApi.deleteDocument(teamId!, examId!, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam'] });
      queryClient.invalidateQueries({ queryKey: ['examReadiness'] });
      toast.success('Document deleted');
    },
  });

  // ─── Findings ───────────────────────────────────────────────────────────────
  const [findingForm, setFindingForm] = useState({
    findingType: 'OBSERVATION',
    severity: 'MEDIUM',
    category: '',
    description: '',
    regulatoryReference: '',
    remediationPlan: '',
    remediationOwner: '',
    remediationDeadline: '',
  });

  const addFindingMutation = useMutation({
    mutationFn: (data: any) => examApi.createFinding(teamId!, examId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam'] });
      toast.success('Finding created');
      setShowAddFinding(false);
      setFindingForm({
        findingType: 'OBSERVATION',
        severity: 'MEDIUM',
        category: '',
        description: '',
        regulatoryReference: '',
        remediationPlan: '',
        remediationOwner: '',
        remediationDeadline: '',
      });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed'),
  });

  const remediationMutation = useMutation({
    mutationFn: ({ findingId, status }: { findingId: string; status: string }) =>
      examApi.updateRemediation(teamId!, examId!, findingId, { remediationStatus: status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam'] });
      toast.success('Remediation status updated');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed'),
  });

  // ─── Requests ───────────────────────────────────────────────────────────────
  const [requestForm, setRequestForm] = useState({
    requestedBy: '',
    description: '',
    category: '',
    priority: 'NORMAL',
    dueDate: '',
  });

  const addRequestMutation = useMutation({
    mutationFn: (data: any) => examApi.createRequest(teamId!, examId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam'] });
      toast.success('Request logged');
      setShowAddRequest(false);
      setRequestForm({ requestedBy: '', description: '', category: '', priority: 'NORMAL', dueDate: '' });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed'),
  });

  const fulfillMutation = useMutation({
    mutationFn: (reqId: string) => examApi.fulfillRequest(teamId!, examId!, reqId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam'] });
      toast.success('Request fulfilled');
    },
  });

  if (isLoading || !exam) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[exam.status] || STATUS_CONFIG.PLANNING;
  const nextStatuses = STATUS_TRANSITIONS[exam.status] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/exams')} className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="h-4 w-4" /> Back to Exams
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {EXAM_TYPES[exam.examType] || exam.examType} — {exam.examinerAgency}
            </h1>
            <div className="mt-2 flex items-center gap-3">
              <span className={clsx('inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium', statusCfg.className)}>
                {statusCfg.label}
              </span>
              {exam.leadExaminer && (
                <span className="text-sm text-gray-500">Lead: {exam.leadExaminer}</span>
              )}
            </div>
          </div>
          {nextStatuses.length > 0 && (
            <Listbox value="" onChange={(s: string) => statusMutation.mutate(s)}>
              <div className="relative">
                <Listbox.Button className="rounded-lg border bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Advance Status
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute right-0 z-10 mt-1 w-48 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {nextStatuses.map((s) => (
                      <Listbox.Option
                        key={s}
                        value={s}
                        className={({ active }) =>
                          clsx('cursor-pointer px-4 py-2 text-sm', active ? 'bg-blue-50 text-blue-700' : 'text-gray-700')
                        }
                      >
                        {STATUS_CONFIG[s]?.label || s}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          )}
        </div>
      </div>

      {/* Readiness bar */}
      {readiness && (
        <div className="rounded-lg border bg-white p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700">Exam Readiness</span>
            <span className="font-bold text-gray-900">{readiness.readinessScore}%</span>
          </div>
          <div className="mt-2 h-3 w-full rounded-full bg-gray-200">
            <div
              className={clsx(
                'h-3 rounded-full transition-all',
                readiness.readinessScore >= 80 ? 'bg-green-500' : readiness.readinessScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'
              )}
              style={{ width: `${readiness.readinessScore}%` }}
            />
          </div>
          <div className="mt-2 flex gap-6 text-xs text-gray-500">
            <span>Docs: {readiness.readyDocuments}/{readiness.totalDocuments} ready</span>
            <span>Open Findings: {readiness.openFindings}</span>
            <span>Unresolved Requests: {readiness.unresolvedRequests}</span>
            {readiness.daysUntilExam !== null && (
              <span className={readiness.daysUntilExam <= 7 ? 'font-semibold text-red-600' : ''}>
                {readiness.daysUntilExam > 0 ? `${readiness.daysUntilExam} days until exam` : 'Exam date passed'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tab.Group>
        <Tab.List className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {['Overview', 'Documents', 'Findings', 'Requests'].map((tab) => (
            <Tab
              key={tab}
              className={({ selected }) =>
                clsx(
                  'w-full rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  selected ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'
                )
              }
            >
              {tab}
              {tab === 'Documents' && exam.documents?.length > 0 && (
                <span className="ml-1.5 text-xs text-gray-400">({exam.documents.length})</span>
              )}
              {tab === 'Findings' && exam.findings?.length > 0 && (
                <span className="ml-1.5 text-xs text-gray-400">({exam.findings.length})</span>
              )}
              {tab === 'Requests' && exam.requests?.length > 0 && (
                <span className="ml-1.5 text-xs text-gray-400">({exam.requests.length})</span>
              )}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-4">
          {/* ─── Overview ─────────────────────────────────────────────────── */}
          <Tab.Panel>
            <div className="rounded-lg border bg-white p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Exam Details</h3>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Type</dt>
                      <dd className="font-medium text-gray-900">{EXAM_TYPES[exam.examType]}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Agency</dt>
                      <dd className="font-medium text-gray-900">{exam.examinerAgency}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Scheduled Start</dt>
                      <dd className="font-medium text-gray-900">
                        {exam.scheduledStartDate ? new Date(exam.scheduledStartDate).toLocaleDateString() : '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Scheduled End</dt>
                      <dd className="font-medium text-gray-900">
                        {exam.scheduledEndDate ? new Date(exam.scheduledEndDate).toLocaleDateString() : '—'}
                      </dd>
                    </div>
                    {exam.actualStartDate && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Actual Start</dt>
                        <dd className="font-medium text-gray-900">{new Date(exam.actualStartDate).toLocaleDateString()}</dd>
                      </div>
                    )}
                    {exam.actualEndDate && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Actual End</dt>
                        <dd className="font-medium text-gray-900">{new Date(exam.actualEndDate).toLocaleDateString()}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Examiner Info</h3>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Lead Examiner</dt>
                      <dd className="font-medium text-gray-900">{exam.leadExaminer || '—'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Contact</dt>
                      <dd className="font-medium text-gray-900">{exam.leadExaminerContact || '—'}</dd>
                    </div>
                  </dl>
                  {exam.focusAreas && (exam.focusAreas as string[]).length > 0 && (
                    <div>
                      <h4 className="text-sm text-gray-500">Focus Areas</h4>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {(exam.focusAreas as string[]).map((area: string) => (
                          <span key={area} className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {exam.notes && (
                    <div>
                      <h4 className="text-sm text-gray-500">Notes</h4>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{exam.notes}</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Readiness by category */}
              {readiness && Object.keys(readiness.categoryCoverage).length > 0 && (
                <div className="mt-6 border-t pt-6">
                  <h3 className="font-semibold text-gray-900">Readiness by Category</h3>
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {Object.entries(readiness.categoryCoverage).map(([cat, data]: [string, any]) => (
                      <div key={cat} className="rounded-lg border p-3">
                        <p className="text-xs font-medium text-gray-500">
                          {DOC_CATEGORIES.find((c) => c.value === cat)?.label || cat}
                        </p>
                        <p className="mt-1 text-lg font-bold text-gray-900">
                          {data.ready}/{data.total}
                        </p>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200">
                          <div
                            className="h-1.5 rounded-full bg-green-500"
                            style={{ width: `${data.total > 0 ? (data.ready / data.total) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Tab.Panel>

          {/* ─── Documents ────────────────────────────────────────────────── */}
          <Tab.Panel>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Documents ({exam.documents?.length || 0})</h3>
              <button
                onClick={() => setShowAddDoc(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
              >
                <PlusIcon className="h-4 w-4" /> Add Document
              </button>
            </div>
            <div className="rounded-lg border bg-white">
              {!exam.documents?.length ? (
                <div className="py-8 text-center text-sm text-gray-500">No documents yet. Add documents to track exam readiness.</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Document</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {exam.documents.map((doc: any) => {
                      const ds = DOC_STATUS_CONFIG[doc.status] || DOC_STATUS_CONFIG.PENDING_PREPARATION;
                      const nextStatusMap: Record<string, string> = {
                        PENDING_PREPARATION: 'READY',
                        READY: 'SHARED_WITH_EXAMINER',
                        SHARED_WITH_EXAMINER: 'ACKNOWLEDGED',
                      };
                      const nextStatus = nextStatusMap[doc.status];
                      return (
                        <tr key={doc.id}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{doc.documentName}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {DOC_CATEGORIES.find((c) => c.value === doc.documentCategory)?.label || doc.documentCategory}
                          </td>
                          <td className="px-4 py-3">
                            <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', ds.className)}>
                              {ds.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {nextStatus && (
                                <button
                                  onClick={() => docStatusMutation.mutate({ docId: doc.id, status: nextStatus })}
                                  className="text-xs font-medium text-blue-600 hover:underline"
                                >
                                  {nextStatus === 'READY' ? 'Mark Ready' : nextStatus === 'SHARED_WITH_EXAMINER' ? 'Mark Shared' : 'Acknowledge'}
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (confirm('Delete this document?')) deleteDocMutation.mutate(doc.id);
                                }}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </Tab.Panel>

          {/* ─── Findings ─────────────────────────────────────────────────── */}
          <Tab.Panel>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Findings ({exam.findings?.length || 0})</h3>
              <button
                onClick={() => setShowAddFinding(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
              >
                <PlusIcon className="h-4 w-4" /> Add Finding
              </button>
            </div>
            <div className="space-y-3">
              {!exam.findings?.length ? (
                <div className="rounded-lg border bg-white py-8 text-center text-sm text-gray-500">
                  No findings recorded yet.
                </div>
              ) : (
                exam.findings.map((f: any) => {
                  const sev = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.MEDIUM;
                  const rem = REMEDIATION_CONFIG[f.remediationStatus] || REMEDIATION_CONFIG.OPEN;
                  const nextRemMap: Record<string, string> = {
                    OPEN: 'IN_PROGRESS',
                    IN_PROGRESS: 'REMEDIATED',
                    REMEDIATED: 'VERIFIED',
                    VERIFIED: 'CLOSED',
                  };
                  const nextRem = nextRemMap[f.remediationStatus];
                  return (
                    <div key={f.id} className="rounded-lg border bg-white p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium', sev.className)}>{sev.label}</span>
                            <span className="text-xs font-medium text-gray-500">{f.findingType.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-gray-400">{f.category}</span>
                          </div>
                          <p className="mt-2 text-sm text-gray-900">{f.description}</p>
                          {f.regulatoryReference && (
                            <p className="mt-1 text-xs text-gray-500">Reg. Ref: {f.regulatoryReference}</p>
                          )}
                          {f.remediationPlan && (
                            <p className="mt-2 text-xs text-gray-600"><span className="font-medium">Remediation:</span> {f.remediationPlan}</p>
                          )}
                          {f.remediationOwner && (
                            <p className="text-xs text-gray-500">Owner: {f.remediationOwner}</p>
                          )}
                          {f.remediationDeadline && (
                            <p className="text-xs text-gray-500">Deadline: {new Date(f.remediationDeadline).toLocaleDateString()}</p>
                          )}
                        </div>
                        <div className="ml-4 flex flex-col items-end gap-2">
                          <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium', rem.className)}>{rem.label}</span>
                          {nextRem && (
                            <button
                              onClick={() => remediationMutation.mutate({ findingId: f.id, status: nextRem })}
                              className="text-xs font-medium text-blue-600 hover:underline"
                            >
                              {REMEDIATION_CONFIG[nextRem]?.label || nextRem}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Tab.Panel>

          {/* ─── Requests ─────────────────────────────────────────────────── */}
          <Tab.Panel>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Examiner Requests ({exam.requests?.length || 0})</h3>
              <button
                onClick={() => setShowAddRequest(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
              >
                <PlusIcon className="h-4 w-4" /> Log Request
              </button>
            </div>
            <div className="rounded-lg border bg-white">
              {!exam.requests?.length ? (
                <div className="py-8 text-center text-sm text-gray-500">No examiner requests logged yet.</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">From</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Priority</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Age</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {exam.requests.map((r: any) => {
                      const rs = REQUEST_STATUS_CONFIG[r.status] || REQUEST_STATUS_CONFIG.RECEIVED;
                      const prio = PRIORITY_CONFIG[r.priority] || PRIORITY_CONFIG.NORMAL;
                      const ageDays = Math.floor((Date.now() - new Date(r.requestedAt).getTime()) / (1000 * 60 * 60 * 24));
                      return (
                        <tr key={r.id}>
                          <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-900">{r.description}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{r.requestedBy}</td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span className={clsx('text-xs', prio.className)}>{prio.label}</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', rs.className)}>{rs.label}</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{ageDays}d</td>
                          <td className="whitespace-nowrap px-4 py-3">
                            {r.status !== 'FULFILLED' && r.status !== 'UNABLE_TO_FULFILL' && (
                              <button
                                onClick={() => fulfillMutation.mutate(r.id)}
                                className="text-xs font-medium text-green-600 hover:underline"
                              >
                                Fulfill
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* ─── Add Document Modal ───────────────────────────────────────────── */}
      <Transition appear show={showAddDoc} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowAddDoc(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Add Document</Dialog.Title>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Document Name</label>
                      <input type="text" value={docForm.documentName} onChange={(e) => setDocForm({ ...docForm, documentName: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <select value={docForm.documentCategory} onChange={(e) => setDocForm({ ...docForm, documentCategory: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                        {DOC_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notes</label>
                      <textarea value={docForm.notes} onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })} rows={2} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button onClick={() => setShowAddDoc(false)} className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button
                      onClick={() => addDocMutation.mutate({ documentName: docForm.documentName, documentCategory: docForm.documentCategory, notes: docForm.notes || undefined })}
                      disabled={!docForm.documentName || addDocMutation.isPending}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                    >
                      {addDocMutation.isPending ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* ─── Add Finding Modal ────────────────────────────────────────────── */}
      <Transition appear show={showAddFinding} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowAddFinding(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Add Finding</Dialog.Title>
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Type</label>
                        <select value={findingForm.findingType} onChange={(e) => setFindingForm({ ...findingForm, findingType: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                          <option value="VIOLATION">Violation</option>
                          <option value="RECOMMENDATION">Recommendation</option>
                          <option value="OBSERVATION">Observation</option>
                          <option value="MATTER_REQUIRING_ATTENTION">Matter Requiring Attention</option>
                          <option value="APPARENT_VIOLATION">Apparent Violation</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Severity</label>
                        <select value={findingForm.severity} onChange={(e) => setFindingForm({ ...findingForm, severity: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                          <option value="CRITICAL">Critical</option>
                          <option value="HIGH">High</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="LOW">Low</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <input type="text" value={findingForm.category} onChange={(e) => setFindingForm({ ...findingForm, category: e.target.value })} placeholder="e.g., Marketing, HMDA" className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea value={findingForm.description} onChange={(e) => setFindingForm({ ...findingForm, description: e.target.value })} rows={3} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Regulatory Reference</label>
                      <input type="text" value={findingForm.regulatoryReference} onChange={(e) => setFindingForm({ ...findingForm, regulatoryReference: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Remediation Plan</label>
                      <textarea value={findingForm.remediationPlan} onChange={(e) => setFindingForm({ ...findingForm, remediationPlan: e.target.value })} rows={2} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Owner</label>
                        <input type="text" value={findingForm.remediationOwner} onChange={(e) => setFindingForm({ ...findingForm, remediationOwner: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Deadline</label>
                        <input type="date" value={findingForm.remediationDeadline} onChange={(e) => setFindingForm({ ...findingForm, remediationDeadline: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button onClick={() => setShowAddFinding(false)} className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button
                      onClick={() => addFindingMutation.mutate({
                        findingType: findingForm.findingType,
                        severity: findingForm.severity,
                        category: findingForm.category,
                        description: findingForm.description,
                        regulatoryReference: findingForm.regulatoryReference || undefined,
                        remediationPlan: findingForm.remediationPlan || undefined,
                        remediationOwner: findingForm.remediationOwner || undefined,
                        remediationDeadline: findingForm.remediationDeadline || undefined,
                      })}
                      disabled={!findingForm.category || !findingForm.description || addFindingMutation.isPending}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                    >
                      {addFindingMutation.isPending ? 'Creating...' : 'Create Finding'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* ─── Add Request Modal ────────────────────────────────────────────── */}
      <Transition appear show={showAddRequest} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowAddRequest(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Log Examiner Request</Dialog.Title>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Requested By</label>
                      <input type="text" value={requestForm.requestedBy} onChange={(e) => setRequestForm({ ...requestForm, requestedBy: e.target.value })} placeholder="Examiner name" className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea value={requestForm.description} onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })} rows={3} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Priority</label>
                        <select value={requestForm.priority} onChange={(e) => setRequestForm({ ...requestForm, priority: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                          <option value="URGENT">Urgent</option>
                          <option value="NORMAL">Normal</option>
                          <option value="LOW">Low</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Due Date</label>
                        <input type="date" value={requestForm.dueDate} onChange={(e) => setRequestForm({ ...requestForm, dueDate: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <input type="text" value={requestForm.category} onChange={(e) => setRequestForm({ ...requestForm, category: e.target.value })} placeholder="Optional" className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button onClick={() => setShowAddRequest(false)} className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button
                      onClick={() => addRequestMutation.mutate({
                        requestedBy: requestForm.requestedBy,
                        description: requestForm.description,
                        category: requestForm.category || undefined,
                        priority: requestForm.priority,
                        dueDate: requestForm.dueDate || undefined,
                      })}
                      disabled={!requestForm.requestedBy || !requestForm.description || addRequestMutation.isPending}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                    >
                      {addRequestMutation.isPending ? 'Logging...' : 'Log Request'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
